// The top bar's facility switcher — the session's facilities as a SideNav-shaped tree.
import { FACILITY_KINDS, buildFacilityValue, parseFacilityValue } from './formOptions';

// Every facility the user can see; the server stores it as no facility selected at all.
export const ALL_FACILITIES = 'all';

/**
 * One heading per kind of facility. Add a kind by adding a row: the switcher, the open
 * path and the label lookup all read this, and nothing else knows the list.
 */
const FACILITY_GROUPS = [
  { kind: FACILITY_KINDS.site, labelKey: 'WORKSPACE_SITES', from: 'sites' },
  { kind: FACILITY_KINDS.storage, labelKey: 'WORKSPACE_STORAGES', from: 'storages' },
  {
    kind: FACILITY_KINDS.backToBack,
    labelKey: 'WORKSPACE_BACK_TO_BACK_ACCOUNTS',
    from: 'backToBackAccounts',
  },
];

// A heading is a row you can only open; `value` is what makes a row selectable.
const groupId = (kind) => `group:${kind}`;

// A terminal's row carries its site too: the server scopes a terminal pick to both.
const TERMINAL_KIND = 'terminal';

export const buildTerminalValue = (siteId, terminalId) =>
  buildFacilityValue(TERMINAL_KIND, buildFacilityValue(siteId, terminalId));

export const parseTerminalValue = (value) => {
  const { kind, id } = parseFacilityValue(value);
  if (kind !== TERMINAL_KIND) return null;
  const { kind: siteId, id: terminalId } = parseFacilityValue(id);
  return siteId && terminalId ? { siteId, terminalId } : null;
};

const terminalNodes = (t, site) =>
  (site.terminals ?? []).map((terminal) => ({
    id: buildTerminalValue(site.id, terminal.id),
    value: buildTerminalValue(site.id, terminal.id),
    label: terminal.name,
    selectedLabel: t('WORKSPACE_SITE_TERMINAL', { site: site.name, terminal: terminal.name }),
  }));

/** The switcher's rows: All, then one heading per kind; a site with terminals expands too. */
export const buildFacilityNodes = (t, session = {}) => [
  { id: ALL_FACILITIES, value: ALL_FACILITIES, label: t('WORKSPACE_ALL_FACILITIES') },
  ...FACILITY_GROUPS.filter((group) => (session[group.from] ?? []).length > 0).map((group) => ({
    id: groupId(group.kind),
    label: t(group.labelKey),
    children: (session[group.from] ?? []).map((item) => ({
      id: buildFacilityValue(group.kind, item.id),
      value: buildFacilityValue(group.kind, item.id),
      label: item.name,
      children: group.kind === FACILITY_KINDS.site ? terminalNodes(t, item) : undefined,
    })),
  })),
];

/** Which row the session's selection is on; the ids below are mutually exclusive. */
export const selectedFacilityValue = (selection) => {
  if (selection?.siteId && selection?.terminalId) {
    return buildTerminalValue(selection.siteId, selection.terminalId);
  }
  if (selection?.siteId) return buildFacilityValue(FACILITY_KINDS.site, selection.siteId);
  if (selection?.storageId) return buildFacilityValue(FACILITY_KINDS.storage, selection.storageId);
  if (selection?.backToBackAccountId) {
    return buildFacilityValue(FACILITY_KINDS.backToBack, selection.backToBackAccountId);
  }
  return ALL_FACILITIES;
};

const findNode = (nodes, value) => {
  for (const node of nodes) {
    if (node.value === value) return node;
    const child = findNode(node.children ?? [], value);
    if (child) return child;
  }
  return null;
};

/** The trigger's text. A selection the session no longer lists reads as All, never blank. */
export const facilityLabel = (value, nodes, allLabel) => {
  const node = findNode(nodes, value);
  return node?.selectedLabel ?? node?.label ?? allLabel;
};

/** The heading above the selected row, so it is visible the first time the menu opens. */
export const openPathFor = (value) => {
  const terminal = parseTerminalValue(value);
  if (terminal) {
    return [groupId(FACILITY_KINDS.site), buildFacilityValue(FACILITY_KINDS.site, terminal.siteId)];
  }
  const { kind } = parseFacilityValue(value);
  return FACILITY_GROUPS.some((group) => group.kind === kind) ? [groupId(kind)] : [];
};
